import {
  type DayOfEmergencyDto,
  type DayOfEmergencyTimelineEntryDto,
  fetchDayOfEmergencyAnonymousAccessGetBubbleContextForLawEnforcement,
  fetchDayOfEmergencyAnonymousAccessListAdminContactsForLawEnforcement,
  fetchDayOfEmergencyAnonymousAccessListMapLocationsForLawEnforcement,
  fetchDayOfEmergencyAnonymousAccessPatchParticipantForLawEnforcement,
  fetchDayOfEmergencyAnonymousAccessValidateLawEnforcement,
} from "@crowdcoursing/api/openapi";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useOperationalMapRealtime } from "~hooks/useOperationalMapRealtime";
import { useToast } from "~hooks/useToast";
import {
  formatLastSchoolwideStatusRequestSummary,
  readLastSchoolwideStatusRequest,
} from "~services/lastSchoolwideStatusRequest";
import { lawEnforcementCommandCenterQueryKeys } from "~services/lawEnforcementCommandCenterQueryKeys";
import {
  fetchLawEnforcementEmergencyActiveDeclarations,
  fetchLawEnforcementEmergencyDay,
  fetchLawEnforcementEmergencyParticipants,
  fetchLawEnforcementEmergencyTimeline,
  LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER,
  postLawEnforcementRequestSchoolwideSafetyStatusSms,
} from "~services/lawEnforcementEmergencyCommandCenterApi";
import { useBackendUrl } from "~services/providers/BackendUrlProvider";
import { ActiveEmergencyDeclarationsSection } from "~ui/organisms/ActiveEmergencyDeclarationsSection/ActiveEmergencyDeclarationsSection";
import { EmergencyMapReadOnly } from "~ui/organisms/EmergencyMapReadOnly/EmergencyMapReadOnly";
import { LawEnforcementChat } from "~ui/organisms/LawEnforcementChat/LawEnforcementChat";
import { ParticipantRosterCard } from "~ui/organisms/ParticipantRosterCard/ParticipantRosterCard";

import {
  canonicalEmergencyStatusKey,
  EVERYONE_SUMMARY_GROUPS,
  KNOWN_STATUS_VALUES,
  OTHER_LEGACY_SUMMARY_ID,
  participantMatchesSummaryFilter,
  type ParticipantRow,
  rosterMatchesSearch,
  STATUS_OPTIONS,
  TIMELINE_ENTRY_KINDS,
  timelineEntryMatchesKindFilter,
  timelineKindLabel,
} from "../VisitorManagement/emergencyCommandCenterShared";
import { EmergencyParticipantStatusDialog } from "../VisitorManagement/EmergencyParticipantStatusDialog";
import { LawEnforcementBubbleContextSection } from "./LawEnforcementBubbleContextSection";

type TabKey = "everyone" | "timeline" | "map" | "chat";

export function LawEnforcementCommandCenterPage() {
  const { dayOfEmergencyId = "", token = "" } = useParams<{ dayOfEmergencyId: string; token: string }>();
  const theme = useTheme();
  const { backendUrl } = useBackendUrl();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>("everyone");
  const [rosterSearch, setRosterSearch] = useState("");
  const [summaryFilterIds, setSummaryFilterIds] = useState<string[]>([]);
  /** Empty = all kinds; otherwise show entries matching any selected kind (OR). */
  const [timelineKindFilterIds, setTimelineKindFilterIds] = useState<string[]>([]);
  const [participantDialog, setParticipantDialog] = useState<ParticipantRow | null>(null);
  const [participantDialogError, setParticipantDialogError] = useState<string | null>(null);
  const [participantDialogSaving, setParticipantDialogSaving] = useState(false);
  const [includeEmergencyContactsSms, setIncludeEmergencyContactsSms] = useState(false);
  const [requestAllStatusesPending, setRequestAllStatusesPending] = useState(false);

  const hasRoute = Boolean(dayOfEmergencyId && token);
  const statusDialogOpen = Boolean(participantDialog);

  const validationQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessValidateLawEnforcement(
        {
          baseUrlOverride: backendUrl,
          body: { token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: lawEnforcementCommandCenterQueryKeys.validate(dayOfEmergencyId, token),
    retry: false,
  });

  const lawEnforcementUserId = validationQuery.data?.lawEnforcementUserId ?? undefined;

  const dayQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) => fetchLawEnforcementEmergencyDay(backendUrl ?? "", dayOfEmergencyId, token, signal),
    queryKey: lawEnforcementCommandCenterQueryKeys.day(dayOfEmergencyId, token),
    refetchInterval: 30_000,
    retry: false,
  });

  const dayId = dayQuery.data?.id;
  const hasDay = Boolean(dayId);

  const lastSchoolwideStatusRequestSummary = useMemo(
    () => (dayQuery.data ? readLastSchoolwideStatusRequest(dayQuery.data as DayOfEmergencyDto) : null),
    [dayQuery.data],
  );

  const participantsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute && hasDay),
    queryFn: ({ signal }) =>
      fetchLawEnforcementEmergencyParticipants(backendUrl ?? "", dayOfEmergencyId, token, signal),
    queryKey: lawEnforcementCommandCenterQueryKeys.participants(dayOfEmergencyId, token),
    refetchInterval: tab === "everyone" || tab === "chat" || tab === "map" ? 10_000 : false,
  });

  const timelineQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute && hasDay) && tab === "timeline",
    queryFn: ({ signal }) => fetchLawEnforcementEmergencyTimeline(backendUrl ?? "", dayOfEmergencyId, token, signal),
    queryKey: lawEnforcementCommandCenterQueryKeys.timeline(dayOfEmergencyId, token),
    refetchInterval: tab === "timeline" ? 15_000 : false,
    retry: false,
  });

  const activeDeclarationsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchLawEnforcementEmergencyActiveDeclarations(backendUrl ?? "", dayOfEmergencyId, token, signal),
    queryKey: lawEnforcementCommandCenterQueryKeys.declarations(dayOfEmergencyId, token),
    refetchInterval: 15_000,
  });

  const bubbleContextQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessGetBubbleContextForLawEnforcement(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: lawEnforcementCommandCenterQueryKeys.bubbleContext(dayOfEmergencyId, token),
    refetchInterval: 60_000,
    retry: false,
  });

  const adminContactsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListAdminContactsForLawEnforcement(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: lawEnforcementCommandCenterQueryKeys.adminContacts(dayOfEmergencyId, token),
    refetchInterval: 60_000,
    retry: false,
  });

  const lawEnforcementMapLocationsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute && hasDay && (statusDialogOpen || tab === "map")),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListMapLocationsForLawEnforcement(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: [...lawEnforcementCommandCenterQueryKeys.root, "map-locations", dayOfEmergencyId, token] as const,
    refetchInterval: tab === "map" ? 60_000 : false,
    retry: false,
  });

  const rosterRows = useMemo(() => (participantsQuery.data ?? []) as ParticipantRow[], [participantsQuery.data]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const o of STATUS_OPTIONS) {
      counts[o.value] = 0;
    }
    let otherUnknownStatuses = 0;
    for (const raw of rosterRows) {
      const key = canonicalEmergencyStatusKey(raw.statusKey ?? "not-reported");
      if (KNOWN_STATUS_VALUES.has(key)) {
        counts[key] += 1;
      } else {
        otherUnknownStatuses += 1;
      }
    }
    return { counts, otherUnknownStatuses };
  }, [rosterRows]);

  const toggleSummaryFilter = useCallback((id: string) => {
    setSummaryFilterIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleTimelineKindFilter = useCallback((kind: string) => {
    setTimelineKindFilterIds((prev) => (prev.includes(kind) ? prev.filter((x) => x !== kind) : [...prev, kind]));
  }, []);

  const filteredRoster = useMemo(() => {
    return rosterRows
      .filter((p) => rosterMatchesSearch(p, rosterSearch))
      .filter((p) => participantMatchesSummaryFilter(p, summaryFilterIds));
  }, [rosterRows, rosterSearch, summaryFilterIds]);

  const timelineEntriesNewestFirst = useMemo(() => {
    const rows = timelineQuery.data ?? [];
    return [...rows].sort((a, b) => {
      const ta = new Date(a.occurredAtUtc ?? 0).getTime();
      const tb = new Date(b.occurredAtUtc ?? 0).getTime();
      return tb - ta;
    });
  }, [timelineQuery.data]);

  const filteredTimelineEntries = useMemo(() => {
    return timelineEntriesNewestFirst.filter((e) => timelineEntryMatchesKindFilter(e.kind, timelineKindFilterIds));
  }, [timelineEntriesNewestFirst, timelineKindFilterIds]);

  const loadFailed = dayQuery.isError;
  const unauthorized =
    dayQuery.error &&
    typeof dayQuery.error === "object" &&
    "status" in dayQuery.error &&
    (dayQuery.error as { status: number }).status === 401;

  const invalidateAll = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: lawEnforcementCommandCenterQueryKeys.root });
  }, [queryClient]);

  const handleRequestAllStatuses = useCallback(async () => {
    if (!backendUrl || !dayOfEmergencyId || !token) {
      return;
    }
    setRequestAllStatusesPending(true);
    try {
      const res = await postLawEnforcementRequestSchoolwideSafetyStatusSms(
        backendUrl,
        dayOfEmergencyId,
        token,
        includeEmergencyContactsSms,
      );
      toast.success(
        `SMS: ${res.smsSendAttemptCount ?? 0} sent to phones (${res.distinctUserCount ?? 0} people in scope). ${res.smsSkippedNoPhoneCount ?? 0} skipped with no phone on file.`,
      );
      await queryClient.invalidateQueries({
        queryKey: lawEnforcementCommandCenterQueryKeys.day(dayOfEmergencyId, token),
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send schoolwide status request.");
    } finally {
      setRequestAllStatusesPending(false);
    }
  }, [backendUrl, dayOfEmergencyId, includeEmergencyContactsSms, queryClient, token, toast]);

  const invalidateOperationalMapQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: lawEnforcementCommandCenterQueryKeys.participants(dayOfEmergencyId, token),
    });
    await queryClient.invalidateQueries({
      queryKey: lawEnforcementCommandCenterQueryKeys.timeline(dayOfEmergencyId, token),
    });
    await queryClient.invalidateQueries({
      queryKey: [...lawEnforcementCommandCenterQueryKeys.root, "map-locations", dayOfEmergencyId, token],
    });
  }, [dayOfEmergencyId, queryClient, token]);

  useOperationalMapRealtime({
    accessToken: token,
    dayOfEmergencyId,
    enabled: hasRoute && hasDay,
    mode: "lawEnforcement",
    onParticipantStatusChanged: invalidateOperationalMapQueries,
  });

  const handleSaveParticipantStatus = useCallback(
    async (payload: {
      bubbleMapLocationId: string | null;
      statusKey: string;
      reportedLatitude: number | null;
      reportedLongitude: number | null;
    }) => {
      if (!backendUrl || !participantDialog?.userId) {
        return;
      }
      setParticipantDialogSaving(true);
      setParticipantDialogError(null);
      try {
        await fetchDayOfEmergencyAnonymousAccessPatchParticipantForLawEnforcement({
          baseUrlOverride: backendUrl,
          body: {
            bubbleMapLocationId: payload.bubbleMapLocationId,
            reportedLatitude: payload.reportedLatitude,
            reportedLongitude: payload.reportedLongitude,
            statusKey: payload.statusKey,
          },
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId, userId: String(participantDialog.userId) },
        });
        await queryClient.invalidateQueries({
          queryKey: lawEnforcementCommandCenterQueryKeys.participants(dayOfEmergencyId, token),
        });
        await queryClient.invalidateQueries({
          queryKey: lawEnforcementCommandCenterQueryKeys.timeline(dayOfEmergencyId, token),
        });
        setParticipantDialog(null);
      } catch (e) {
        setParticipantDialogError(e instanceof Error ? e.message : "Could not update status.");
      } finally {
        setParticipantDialogSaving(false);
      }
    },
    [backendUrl, dayOfEmergencyId, participantDialog?.userId, queryClient, token],
  );

  if (!hasRoute) {
    return (
      <Box sx={{ boxSizing: "border-box", maxWidth: 720, minWidth: 0, mx: "auto", p: { md: 3, sm: 2, xs: 1 } }}>
        <Alert severity="warning">
          This link is missing required parameters. Use the full URL from your notification.
        </Alert>
      </Box>
    );
  }

  if (!backendUrl) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        maxWidth: "100%",
        minWidth: 0,
        mx: "auto",
        p: { md: 3, sm: 2, xs: 1 },
        width: "100%",
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{ gap: 1, mb: 2, minWidth: 0 }}
      >
        <Typography component="h1" sx={{ minWidth: 0 }} variant="h5">
          Law Enforcement Command Center
        </Typography>
        {hasDay ? (
          <Button onClick={() => void invalidateAll()} size="small" variant="outlined">
            Refresh data
          </Button>
        ) : null}
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        Opened via secure link.
      </Typography>

      <LawEnforcementBubbleContextSection
        adminContactsQuery={adminContactsQuery}
        bubbleContextQuery={bubbleContextQuery}
      />

      <ActiveEmergencyDeclarationsSection declarations={activeDeclarationsQuery.data ?? []} />

      {hasDay && dayQuery.data ? (
        <Card sx={{ mb: 2, p: 2 }} variant="outlined">
          <Typography sx={{ mb: 1 }} variant="subtitle1">
            Request status from everyone
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
            Sends a text to each person with a role in this school with a short link to report their safety and
            location. Optionally include bubble admins who may not appear in the role roster. Law enforcement is not
            messaged here.
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={includeEmergencyContactsSms} onChange={(_, v) => setIncludeEmergencyContactsSms(v)} />
            }
            label="Include bubble admins (not in roster)"
          />
          <Box sx={{ mt: 2 }}>
            <Button
              disabled={requestAllStatusesPending}
              onClick={() => void handleRequestAllStatuses()}
              variant="contained"
            >
              {requestAllStatusesPending ? "Sending…" : "Request All Statuses"}
            </Button>
          </Box>
          <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
            {lastSchoolwideStatusRequestSummary
              ? formatLastSchoolwideStatusRequestSummary(lastSchoolwideStatusRequestSummary)
              : "No schoolwide status request has been logged for this day yet."}
          </Typography>
        </Card>
      ) : null}

      {dayQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {loadFailed ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {unauthorized
            ? "This link is invalid or has expired. Request a new law enforcement link from the school."
            : "Could not load this emergency day. It may have been removed or the link may no longer be valid."}
        </Alert>
      ) : null}

      {hasDay && dayQuery.data ? (
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="caption">
          Date: {String((dayQuery.data as DayOfEmergencyDto).utcCalendarDate)}
        </Typography>
      ) : null}

      {hasDay ? (
        <>
          <Tabs
            allowScrollButtonsMobile
            onChange={(_, v) => setTab(v as TabKey)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            value={tab}
            variant="scrollable"
          >
            <Tab label="Everyone" value="everyone" />
            <Tab label="Timeline" value="timeline" />
            <Tab label="Map" value="map" />
            <Tab label="Chat" value="chat" />
          </Tabs>

          {tab === "everyone" ? (
            <Stack spacing={2}>
              {participantsQuery.isLoading ? (
                <CircularProgress size={32} />
              ) : participantsQuery.isError ? (
                <Alert severity="error">Could not load participants.</Alert>
              ) : (
                <>
                  <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                    <Typography color="text.secondary" component="span" variant="caption">
                      Click summary cards to filter the roster (select several to combine). Click again to remove a
                      filter.
                    </Typography>
                    {summaryFilterIds.length > 0 ? (
                      <Button onClick={() => setSummaryFilterIds([])} size="small" variant="text">
                        Clear status filters
                      </Button>
                    ) : null}
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.5,
                      gridTemplateColumns: {
                        md: "repeat(4, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        xl: "repeat(6, minmax(0, 1fr))",
                        xs: "repeat(2, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {EVERYONE_SUMMARY_GROUPS.map((g) => {
                      const selected = summaryFilterIds.includes(g.id);
                      return (
                        <Card
                          key={g.id}
                          variant="outlined"
                          sx={{
                            bgcolor: selected ? "action.selected" : undefined,
                            borderColor: selected ? "primary.main" : undefined,
                            borderWidth: selected ? 2 : 1,
                          }}
                        >
                          <CardActionArea
                            aria-label={`Filter by ${g.label}`}
                            aria-pressed={selected}
                            onClick={() => toggleSummaryFilter(g.id)}
                            sx={{ borderRadius: 1 }}
                          >
                            <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
                              <Typography color="text.secondary" component="div" variant="caption">
                                {g.label}
                              </Typography>
                              <Typography component="div" sx={{ mt: 0.5 }} variant="h5">
                                {g.statusValues.reduce((sum, v) => sum + (statusCounts.counts[v] ?? 0), 0)}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                    {statusCounts.otherUnknownStatuses > 0 ? (
                      <Card
                        key={OTHER_LEGACY_SUMMARY_ID}
                        variant="outlined"
                        sx={{
                          bgcolor: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? "action.selected" : undefined,
                          borderColor: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? "primary.main" : undefined,
                          borderWidth: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? 2 : 1,
                        }}
                      >
                        <CardActionArea
                          aria-label="Filter by other or legacy status"
                          aria-pressed={summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID)}
                          onClick={() => toggleSummaryFilter(OTHER_LEGACY_SUMMARY_ID)}
                          sx={{ borderRadius: 1 }}
                        >
                          <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
                            <Typography color="text.secondary" component="div" variant="caption">
                              Other / legacy
                            </Typography>
                            <Typography component="div" sx={{ mt: 0.5 }} variant="h5">
                              {statusCounts.otherUnknownStatuses}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ) : null}
                  </Box>

                  <TextField
                    fullWidth
                    onChange={(e) => setRosterSearch(e.target.value)}
                    placeholder="Search by name, phone, or role"
                    size="small"
                    value={rosterSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Typography color="text.secondary" variant="caption">
                    Showing {filteredRoster.length} of {rosterRows.length} people
                    {rosterSearch.trim() || summaryFilterIds.length > 0 ? " (filtered)" : ""}
                  </Typography>

                  {filteredRoster.length === 0 && rosterRows.length > 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No one matches your filters. Try a different search, adjust the status cards above, or clear
                      filters.
                    </Typography>
                  ) : null}

                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.5,
                      gridTemplateColumns: {
                        md: "repeat(4, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        xl: "repeat(5, minmax(0, 1fr))",
                        xs: "repeat(2, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {filteredRoster.map((row) => (
                      <ParticipantRosterCard
                        key={row.userId}
                        compact
                        participant={row}
                        onSelect={(p) => {
                          setParticipantDialog(p);
                          setParticipantDialogError(null);
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Stack>
          ) : null}

          {tab === "timeline" ? (
            <Stack spacing={2}>
              <Typography color="text.secondary" variant="body2">
                Activity for this reunification day (newest first): dispatches, status updates, and emergency plan
                access via shared links.
              </Typography>
              {timelineQuery.isLoading ? (
                <CircularProgress size={32} />
              ) : timelineQuery.isError ? (
                <Alert severity="error">Could not load timeline.</Alert>
              ) : timelineEntriesNewestFirst.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No recorded activity yet for this day.
                </Typography>
              ) : (
                <>
                  <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                    <Typography color="text.secondary" component="span" variant="caption">
                      Filter by activity type (select one or more). Click again to clear a type.
                    </Typography>
                    {timelineKindFilterIds.length > 0 ? (
                      <Button onClick={() => setTimelineKindFilterIds([])} size="small" variant="text">
                        Clear type filters
                      </Button>
                    ) : null}
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {TIMELINE_ENTRY_KINDS.map((kind) => {
                      const selected = timelineKindFilterIds.includes(kind);
                      return (
                        <Chip
                          key={kind}
                          clickable
                          color={selected ? "primary" : "default"}
                          label={timelineKindLabel(kind)}
                          onClick={() => toggleTimelineKindFilter(kind)}
                          size="small"
                          variant={selected ? "filled" : "outlined"}
                        />
                      );
                    })}
                  </Stack>
                  <Typography color="text.secondary" variant="caption">
                    Showing {filteredTimelineEntries.length} of {timelineEntriesNewestFirst.length} activities
                    {timelineKindFilterIds.length > 0 ? " (filtered)" : ""}
                  </Typography>
                  {filteredTimelineEntries.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No activities match the selected types. Clear a filter or choose different types.
                    </Typography>
                  ) : (
                    <Stack component="ol" sx={{ listStyle: "none", m: 0, p: 0 }} spacing={0}>
                      {filteredTimelineEntries.map((e: DayOfEmergencyTimelineEntryDto, i: number) => (
                        <Box
                          component="li"
                          key={`${e.kind}-${e.occurredAtUtc}-${i}`}
                          sx={{
                            "&:last-child": { pb: 0 },
                            borderColor: "divider",
                            borderLeft: 2,
                            ml: 0.5,
                            pb: 2,
                            pl: 2,
                          }}
                        >
                          <Typography color="text.secondary" variant="caption">
                            {new Date(e.occurredAtUtc ?? "").toLocaleString()}
                          </Typography>
                          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                            <Chip label={timelineKindLabel(e.kind ?? "")} size="small" variant="outlined" />
                            {e.actorDisplayName ? (
                              <Typography color="text.secondary" variant="caption">
                                {e.actorDisplayName}
                              </Typography>
                            ) : null}
                          </Stack>
                          <Typography sx={{ mt: 1 }} variant="body2">
                            {e.summary}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          ) : null}

          {tab === "map" ? (
            <EmergencyMapReadOnly
              bubbleId={bubbleContextQuery.data?.bubbleId}
              error={lawEnforcementMapLocationsQuery.isError}
              highlightColor={theme.palette.primary.main}
              loading={lawEnforcementMapLocationsQuery.isLoading}
              locations={lawEnforcementMapLocationsQuery.data ?? []}
              participants={participantsQuery.data ?? []}
              siteName={bubbleContextQuery.data?.displayName ?? undefined}
            />
          ) : null}

          {tab === "chat" ? (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                height: "min(70vh, 560px)",
                minHeight: 360,
                overflow: "hidden",
              }}
            >
              {hasDay ? (
                <LawEnforcementChat
                  accessToken={token}
                  dayOfEmergencyId={dayOfEmergencyId}
                  lawEnforcementUserId={lawEnforcementUserId}
                />
              ) : null}
            </Box>
          ) : null}
        </>
      ) : null}

      <EmergencyParticipantStatusDialog
        errorMessage={participantDialogError}
        mapLocations={(lawEnforcementMapLocationsQuery.data ?? []).map((l) => ({
          id: String(l.id),
          isActive: l.isActive,
          label: l.label ?? "",
          latitude: l.latitude,
          longitude: l.longitude,
          polygonGeoJson: (l as { polygonGeoJson?: string | null }).polygonGeoJson ?? null,
        }))}
        mapLocationsLoading={lawEnforcementMapLocationsQuery.isLoading}
        onClose={() => {
          setParticipantDialog(null);
          setParticipantDialogError(null);
        }}
        onSave={handleSaveParticipantStatus}
        open={statusDialogOpen}
        participant={participantDialog}
        saving={participantDialogSaving}
        title="Update participant status"
      />
    </Box>
  );
}
